/**
 * @fileoverview HeaderTimeTracker — global "Log time" control (MOR-906)
 *
 * Purpose: Puts time capture in the dashboard header so a practitioner can
 * record time from any route without navigating to the timesheets section.
 *
 * Renders one of two states:
 * - Idle: a "Log time" button (opens the quick entry dialog), a plain-English
 *   quick entry button, and a start timer button.
 * - Running: the live elapsed time as a stop button, plus a discard button.
 *   Stopping opens the quick entry dialog with the units already filled in,
 *   along with the start/end clock times the stopwatch measured.
 *
 * Also registers the Cmd/Ctrl+Shift+L shortcut for the dialog.
 *
 * Used by: dashboard `_layout.tsx` header.
 */

import { useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import { Plus, Sparkles, Square, Timer, X } from "lucide-react";

import { Button } from "@/ui/primitives";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { useTimeTracker } from "./TimeTrackerContext";

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const SHORTCUT_LABEL = isMac ? "⌘⇧L" : "Ctrl+Shift+L";

/** Formats elapsed seconds as `m:ss`, growing to `h:mm:ss` past the hour. */
function formatElapsed(totalSec: number): string {
  const seconds = String(totalSec % 60).padStart(2, "0");
  const minutes = Math.floor(totalSec / 60) % 60;
  const hours = Math.floor(totalSec / 3600);
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`;
  }
  return `${minutes}:${seconds}`;
}

export function HeaderTimeTracker() {
  const {
    timer,
    elapsedSec,
    startTimer,
    stopTimer,
    discardTimer,
    openQuickEntry,
    openNlEntry,
  } = useTimeTracker();

  // Starting a timer from a job page remembers the matter, so stopping it
  // elsewhere still logs against the right one.
  const routeParams = useParams({ strict: false }) as { jobId?: string };

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "l"
      ) {
        e.preventDefault();
        openQuickEntry({ jobId: routeParams.jobId });
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openQuickEntry, routeParams.jobId]);

  const handleStop = () => {
    const stopped = stopTimer();
    if (!stopped) return;
    const startedAt = new Date(stopped.startedAt);
    openQuickEntry({
      jobId: stopped.jobId ?? routeParams.jobId,
      elapsedMinutes: stopped.elapsedMinutes,
      // The work belongs to the day it started on, which is not today's date
      // for a session left running over midnight.
      date: format(startedAt, "yyyy-MM-dd"),
      startTime: format(startedAt, "HH:mm"),
      endTime: format(new Date(stopped.stoppedAt), "HH:mm"),
    });
  };

  return (
    <TooltipProvider>
      <div className="ml-auto flex items-center gap-1">
        {timer ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  className="h-8 tabular-nums"
                  onClick={handleStop}
                >
                  <Square className="size-3.5" />
                  {formatElapsed(elapsedSec)}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Stop timer and log the time</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground"
                  onClick={discardTimer}
                  aria-label="Discard timer"
                >
                  <X className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Discard timer</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => openQuickEntry({ jobId: routeParams.jobId })}
                >
                  <Plus className="size-4" />
                  Log time
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Log a time entry ({SHORTCUT_LABEL})
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground"
                  onClick={openNlEntry}
                  aria-label="Quick entry"
                >
                  <Sparkles className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Quick entry — describe your time in plain English
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground"
                  onClick={() => startTimer(routeParams.jobId)}
                  aria-label="Start timer"
                >
                  <Timer className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Start a timer</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
